/** @jsxImportSource ../i18n */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const noop = () => {};
const silent = { success: noop, error: noop, info: noop, dismiss: noop };

const ToastContext = createContext({ toast: silent, toasts: [], pause: noop, resume: noop });

const MAX_TOASTS = 3;
const DURATIONS = { success: 4000, info: 4000, error: 7000 };

let sequence = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const clear = useCallback((id) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
  }, []);

  const dismiss = useCallback((id) => {
    clear(id);
    setToasts((list) => list.filter((t) => t.id !== id));
  }, [clear]);

  const schedule = useCallback((id, ms) => {
    clear(id);
    timers.current.set(id, setTimeout(() => dismiss(id), ms));
  }, [clear, dismiss]);

  const push = useCallback((tone, title, { description, duration } = {}) => {
    sequence += 1;
    const id = sequence;
    const ms = duration ?? DURATIONS[tone];
    setToasts((list) => [...list, { id, tone, title, description, duration: ms }].slice(-MAX_TOASTS));
    schedule(id, ms);
    return id;
  }, [schedule]);

  const pause = useCallback((id) => clear(id), [clear]);
  const resume = useCallback((id) => schedule(id, 2500), [schedule]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => clearTimeout(timer));
  }, []);

  const toast = useMemo(() => ({
    success: (title, options) => push('success', title, options),
    error: (title, options) => push('error', title, options),
    info: (title, options) => push('info', title, options),
    dismiss,
  }), [push, dismiss]);

  const value = useMemo(() => ({ toast, toasts, pause, resume }), [toast, toasts, pause, resume]);
  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => useContext(ToastContext).toast;

export const useToastState = () => useContext(ToastContext);
